import { Component } from 'react';

/**
 * ErrorBoundary: Fängt React-Rendering-Fehler ab und zeigt
 * eine benutzerfreundliche Fehlermeldung statt einem leeren Bildschirm.
 *
 * Verwendung:
 *   <ErrorBoundary>
 *     <MeineKomponente />
 *   </ErrorBoundary>
 *
 * Mit eigenem Fallback:
 *   <ErrorBoundary fallback={<p>Fehler!</p>}>
 *     <MeineKomponente />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('❌ ErrorBoundary gefangen:', error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tda-bg-dark, #0d0d0d)',
        fontFamily: 'inherit',
        padding: '2rem',
      }}>
        <div style={{
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          background: 'var(--tda-bg-surface, #161616)',
          border: '1px solid var(--tda-border-primary, rgba(255,51,51,0.18))',
          borderRadius: '12px',
          padding: '3rem 2rem',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{
            color: 'var(--tda-primary, #ff3333)',
            fontSize: '1.4rem',
            marginBottom: '0.75rem',
          }}>
            Unerwarteter Fehler
          </h2>
          <p style={{
            color: 'var(--tda-text-secondary, rgba(255,255,255,0.7))',
            marginBottom: '0.5rem',
            lineHeight: '1.5',
          }}>
            Ein Fehler ist aufgetreten. Bitte lade die Seite neu.
          </p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{
              background: 'rgba(255,51,51,0.08)',
              border: '1px solid rgba(255,51,51,0.2)',
              borderRadius: '6px',
              padding: '0.75rem',
              fontSize: '0.75rem',
              color: 'var(--tda-primary-light, #ff4444)',
              textAlign: 'left',
              overflowX: 'auto',
              marginBottom: '1.5rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={() => this.handleReset()}
            style={{
              background: 'var(--tda-gradient-primary, linear-gradient(135deg,#ff3333,#cc0000))',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
